/**
 * Registry metadata badges for artifact list rows
 */

import React from 'react';
import { Badge } from '../UI/Badge';
import { ExternalLink, Tag } from 'lucide-react';
import {
  extractRegistryMeta,
  formatDocTypeLabel,
  formatRegistryColumnLabel,
  isExternalRegistryHref,
} from '../../utils/registryArtifactUtils';
import type { Artifact } from '../../types/api';

interface RegistryMetaBadgesProps {
  artifact: Artifact;
  compact?: boolean;
}

export function RegistryMetaBadges({ artifact, compact = false }: RegistryMetaBadgesProps) {
  const reg = extractRegistryMeta(artifact);
  if (!reg) return null;

  const tagLimit = compact ? 2 : 4;
  const visibleTags = (reg.keyword_tags ?? []).slice(0, tagLimit);
  const hiddenTagCount = (reg.keyword_tags?.length ?? 0) - visibleTags.length;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {reg.project_id && (
        <Badge
          variant="primary"
          size="sm"
          className="font-mono"
          title={reg.project_name || reg.project_id}
        >
          {reg.project_id}
        </Badge>
      )}
      {reg.project_name && !compact && (
        <Badge variant="default" size="sm" className="max-w-[240px] truncate" title={reg.project_name}>
          {reg.project_name}
        </Badge>
      )}
      {reg.doc_type && (
        <Badge variant="info" size="sm">
          {formatDocTypeLabel(reg.doc_type)}
        </Badge>
      )}
      {reg.registry_column && !compact && (
        <Badge variant="default" size="sm">
          {formatRegistryColumnLabel(reg.registry_column)}
        </Badge>
      )}
      {reg.client && (
        <Badge variant="default" size="sm">
          {reg.client}
        </Badge>
      )}
      {reg.service_line && (
        <Badge variant="default" size="sm">
          {reg.service_line}
        </Badge>
      )}
      {reg.geography && (
        <Badge variant="default" size="sm">
          {reg.geography}
        </Badge>
      )}
      {reg.registry_source && !compact && (
        <Badge variant="warning" size="sm" className="text-xs">
          {reg.registry_source.replace(/_/g, ' ')}
        </Badge>
      )}
      {visibleTags.map((tag) => (
        <Badge key={tag} variant="success" size="sm">
          <Tag className="mr-0.5 h-3 w-3" />
          {tag}
        </Badge>
      ))}
      {hiddenTagCount > 0 && (
        <Badge variant="default" size="sm">
          +{hiddenTagCount} tags
        </Badge>
      )}
      {reg.registry_href && isExternalRegistryHref(reg.registry_href) && (
        <a
          href={reg.registry_href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-xs text-primary-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
          title={reg.registry_href}
        >
          <ExternalLink className="mr-0.5 h-3 w-3" />
          {formatRegistryColumnLabel(reg.registry_column)}
        </a>
      )}
      {reg.registry_resolved_path && !compact && (
        <span
          className="max-w-[200px] truncate font-mono text-xs text-theme-text-secondary"
          title={reg.registry_resolved_path}
        >
          {reg.registry_resolved_path}
        </span>
      )}
    </div>
  );
}
